import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Shield, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/api";
import { useAuth } from "@/auth/auth-context";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import StepIndicator from "@/components/StepIndicator";
import { ROUTE_PATHS, getDashboardDomainUrl } from "@/routing/domainRoutes";

type AppliedPromo = {
  promoCodeId: string;
  code: string;
  benefitSummary: string;
  originalPrice: number;
  finalPrice: number;
  benefitType?: "DISCOUNT" | "GRANT";
  checkoutRequired?: boolean;
};

type CheckoutPreviewResponse = {
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
  checkoutRequired: boolean;
  executionPath: "STRIPE_CHECKOUT" | "INTERNAL_FINALIZE";
  promoOutcome: "NONE" | "DISCOUNT" | "GRANT";
  message: string;
  appliedPromo: AppliedPromo | null;
};

export default function Payment() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { completePendingSignup } = useAuth();

  const plan = searchParams.get("plan") || "starter";
  const interval =
    plan === "starter"
      ? "MONTHLY"
      : searchParams.get("interval") === "YEARLY"
        ? "YEARLY"
        : "MONTHLY";
  const signupId = searchParams.get("signup") || "";
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState<CheckoutPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const signupDraft = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("workscope-signup-draft");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);
  const appliedPromo = preview?.appliedPromo ?? (signupDraft?.appliedPromo as AppliedPromo | null | undefined) ?? null;
  const originalAmount = preview?.originalAmount ?? appliedPromo?.originalPrice ?? 0;
  const discountAmount = preview?.discountAmount ?? (appliedPromo ? Math.max(0, appliedPromo.originalPrice - appliedPromo.finalPrice) : 0);
  const finalAmount = preview?.finalAmount ?? appliedPromo?.finalPrice ?? 0;
  const checkoutRequired = preview?.checkoutRequired ?? finalAmount > 0;
  const isFreePlan = !checkoutRequired && finalAmount === 0;
  const isFreeViaPromo = Boolean(appliedPromo && !checkoutRequired);
  const ctaLabel = checkoutRequired
    ? t("createAccount.continueToPayment")
    : isFreeViaPromo
      ? t("payment.activateWithPromo")
      : t("payment.continueToWorkspace");
  const nextStepLabel = checkoutRequired
    ? t("payment.nextStepSecureCheckout")
    : isFreeViaPromo
      ? t("payment.nextStepGrant")
      : t("payment.nextStepNoPayment");

  useEffect(() => {
    if (!signupId) return;

    const run = async () => {
      setPreviewLoading(true);
      try {
        const response = await apiRequest<CheckoutPreviewResponse>("/auth/checkout-preview", {
          method: "POST",
          auth: false,
          body: {
            pendingSignupId: signupId,
            planId: plan,
            planInterval: interval,
          },
        });
        setPreview(response);
      } catch (error) {
        toast({
          title: t("payment.unableToCheckout"),
          description: error instanceof Error ? error.message : t("common.tryAgain"),
          variant: "destructive",
        });
      } finally {
        setPreviewLoading(false);
      }
    };

    void run();
  }, [interval, plan, signupId, t, toast]);

  const handleCheckout = async () => {
    if (!signupId) {
      toast({
        title: t("payment.missingSignupSession"),
        description: t("payment.completeCompanyDetails"),
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await apiRequest<{ url: string; mode: string; checkoutRequired?: boolean; message?: string }>("/auth/checkout-session", {
        method: "POST",
        auth: false,
        body: {
          pendingSignupId: signupId,
          planId: plan,
          planInterval: interval,
          successPath: `/success?plan=${plan}&interval=${interval}&signup=${signupId}`,
          cancelPath: `/payment?plan=${plan}&interval=${interval}&signup=${signupId}`,
          baseUrl: window.location.origin,
        },
      });

      if (response.mode === "free" || response.mode === "completed") {
        await completePendingSignup(signupId);
        sessionStorage.removeItem("workscope-signup-draft");
        window.location.replace(getDashboardDomainUrl(ROUTE_PATHS.dashboardHome));
        return;
      }

      window.location.href = response.url;
    } catch (error) {
      toast({
        title: t("payment.unableToCheckout"),
        description: error instanceof Error ? error.message : t("common.tryAgain"),
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <StepIndicator currentStep={3} />
        <div className="container mx-auto px-4 pb-20 lg:px-8">
          <div className="mx-auto grid max-w-4xl items-start gap-10 lg:grid-cols-3">
            <div className="space-y-5 lg:col-span-2">
              <h1 className="text-2xl font-bold text-primary">{t("payment.title")}</h1>

              <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>
                    {t("payment.selectedPlan")}: <span className="font-semibold text-foreground">{t(`plans.${plan}`)}</span>
                  </p>
                  {signupDraft?.companyName ? (
                    <p>
                      {t("createAccount.companyName")}: <span className="font-semibold text-foreground">{signupDraft.companyName}</span>
                    </p>
                  ) : null}
                  {signupDraft?.email ? (
                    <p>
                      {t("login.email")}: <span className="font-semibold text-foreground">{signupDraft.email}</span>
                    </p>
                  ) : null}
                </div>

                <div className="mt-6 flex flex-wrap justify-end gap-3">
                  {checkoutRequired ? (
                    <>
                      <Button variant="outline" asChild disabled={isLoading}>
                        <Link to="/pricing">{t("payment.changePlan")}</Link>
                      </Button>
                      <Button variant="outline" asChild disabled={isLoading}>
                        <Link to={`/create-account?plan=${plan}&interval=${interval}`}>{t("payment.backToCompanyDetails")}</Link>
                      </Button>
                    </>
                  ) : null}
                  <Button
                    onClick={handleCheckout}
                    disabled={isLoading || previewLoading || !preview}
                    className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90"
                  >
                    {checkoutRequired ? <Lock className="h-4 w-4" /> : null}
                    {isLoading ? t("pages.upgradePlan.redirecting") : ctaLabel}
                  </Button>
                </div>

                <div className="mt-5 flex items-center gap-2 text-xs text-muted-foreground">
                  <Shield className="h-4 w-4" />
                  {checkoutRequired
                    ? t("payment.secureCheckoutPoweredByStripe")
                    : isFreePlan
                    ? isFreeViaPromo
                      ? t("payment.noPaymentAfterPromo")
                      : t("payment.noPaymentForStarter")
                    : t("payment.noPaymentRequiredDescription")}
                </div>
              </div>
            </div>

            <div className="h-fit rounded-xl border border-border bg-card p-6 shadow-sm lg:mt-12">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">{t("payment.summary")}</h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {t("payment.planSummary", {
                      plan: t(`plans.${plan}`),
                      billing:
                        plan === "starter"
                          ? t("payment.oneMonthTrial")
                          : interval === "YEARLY"
                            ? t("payment.annual")
                          : t("payment.monthly"),
                    })}
                  </span>
                  <span className="font-medium text-card-foreground">${finalAmount.toFixed(2)}</span>
                </div>
                {appliedPromo ? (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("createAccount.originalPrice")}</span>
                      <span className="font-medium text-card-foreground">${originalAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("createAccount.promoCode")}</span>
                      <span className="font-medium text-emerald-700">{appliedPromo.code}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Discount</span>
                      <span className="font-medium text-emerald-700">- ${discountAmount.toFixed(2)}</span>
                    </div>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                      {appliedPromo.benefitSummary}
                    </div>
                  </>
                ) : null}
                {appliedPromo ? (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t("payment.promoOutcome")}</span>
                    <span className="font-medium text-card-foreground">
                      {preview?.promoOutcome === "GRANT"
                        ? t("payment.grantOutcome")
                        : preview?.promoOutcome === "DISCOUNT"
                          ? t("payment.discountOutcome")
                          : t("payment.standardOutcome")}
                    </span>
                  </div>
                ) : null}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t("payment.nextStep")}</span>
                  <span className="font-medium text-card-foreground">{nextStepLabel}</span>
                </div>
                <div className="flex justify-between border-t border-border pt-3 font-semibold">
                  <span className="text-card-foreground">{t("payment.amountDueNow")}</span>
                  <span className="text-accent">${finalAmount.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
